import { Button } from "primereact/button";
import { useEffect, useState, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Toast, ToastMessage } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { useTranslation } from "react-i18next";
import { POSModel } from '../../models/orders/order-list-model';
import orderServices from '../../services/orders/orderservice';
import '../../assests/css/common/responsive-ui.css'
import AddProducttopos from "./AddProductpos";
import IncomingRequestsDialog from "./incomingRequestDialog";
import posService from "../../services/pos/pos.service";
import { Badge } from "primereact/badge";

interface SearchPanelProps {
  onSearch: (query: string) => void;
  setLoading: (loading: boolean) => void;
  onPOSChange: (posId: any) => void;
  onAddSuccess: (posId: number[]) => void;
  onStatusFilterChange?: (statuses: string[]) => void;
}

const PosSearch: React.FC<SearchPanelProps> = ({
  onSearch,
  setLoading,
  onPOSChange,
  onAddSuccess,
  onStatusFilterChange
}) => {
  const { t } = useTranslation();
  const [keywordsearch, setkeywordsearch] = useState<string>("");
  const [pointOfSale, setPointOfSale] = useState<number[]>([]);
  const [posData, setPOSData] = useState<POSModel[]>([]);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showRequestsDialog, setShowRequestsDialog] = useState(false);
  const [requestCount, setRequestCount] = useState<number>(0);
  const [inwardrequestCount, setinwardRequestCount] = useState<number>(0);
  const [isPOSSelected, setIsPOSSelected] = useState<number[]>([]);
  const totalRequestCount = requestCount + inwardrequestCount;
  const toast = useRef<Toast>(null);
  const statusOptions = ["Low", "Sufficient", "Critical"];
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  const validatePOS = (): boolean => {
    const isValid = !!pointOfSale;
    //setIsPOSSelected(isValid);
    return isValid;
  };

  const handleSearch = () => {
    if (!validatePOS()) {
      showMessage("Error", "Please select a POS", "warn");
      return;
    }
    setLoading(true)
    onSearch(keywordsearch);
    onStatusFilterChange?.(selectedStatuses);
  };

  const resetSearchFields = () => {
    setkeywordsearch("");
    onSearch("");
    posData.length > 1 && setIsPOSSelected([]);
    setSelectedStatuses([]);
  };

  const showMessage = (
    label: string,
    message: string,
    severity: ToastMessage["severity"]
  ) => {
    toast.current?.show({
      severity: severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  const getALLPOS = async () => {
    try {
      const response: POSModel[] = await orderServices.getPOS();
      setPOSData(response);
      // Auto-select if there's only one POS
      if (response.length === 1) {
        const singlePOS = [response[0].posID];
        setPointOfSale(singlePOS);
        onPOSChange(singlePOS);
        refreshRequests(singlePOS);
        setIsPOSSelected(singlePOS);
      }
    } catch (error) {
      showMessage("Error", "Failed to fetch POS data", "error");
    }
  };

  useEffect(() => {
    getALLPOS();
  }, [])

  useEffect(() => {
    const notifyPOSChange = async () => {
      if (Array.isArray(isPOSSelected) && isPOSSelected.length > 0) {
        setPointOfSale(isPOSSelected);
        onPOSChange(isPOSSelected);
        await refreshRequests(isPOSSelected);
      }
      else {
        onPOSChange([]);
        setRequestCount(0);
        setinwardRequestCount(0);
        setIsPOSSelected([]);
      }
    }
    notifyPOSChange();
  }, [JSON.stringify(isPOSSelected)]);


  const handleOnSuccess = () => {
    setShowAddProductModal(false);
    showMessage("Add Products", "Product added successfully", "success");
    onAddSuccess(pointOfSale);
  };


  const handleSendRequest = async (request: any) => {
    try {
      setLoading(true);
      const sendData = {
        requestId: request.id, // from request row
        productId: request.productId,
        posId: request.requestToPOS, // current POS sending
        toPosId: request.requestFromPOS,
        quantity: request.quantity,
        note: request.note || "",
        documentDetails: request.documentDetails || ""
      };
      const response = await posService.UpdatePossend(sendData); // call API
      if (response?.isSuccess) {
        showMessage("Success", response.message, "success");
        // setShowRequestsDialog(false);
        await refreshRequests(pointOfSale);
        onAddSuccess(pointOfSale);
      } else {
        showMessage("Failed", response?.message || "Failed to send product", "error");
      }
    } catch (error) {
      console.error("Error sending product", error);
      showMessage("Error", "An unexpected error occurred", "error");
    } finally {
      setLoading(false); // Always stop loading
    }
  };

  const handleRejectRequest = async (request: any) => {
    try {
      setLoading(true);
      const rejectData = {
        requestId: request.id, // from request row
        productId: request.productId,
        posId: request.requestToPOS, // current POS sending
        toPosId: request.requestFromPOS,
        quantity: request.quantity,
        note: request.note || ""
      };

      const response = await posService.UpdatePosReject(rejectData); // call API

      if (response?.isSuccess) {
        showMessage("Success", response.message, "success");
        // setShowRequestsDialog(false);
        await refreshRequests(pointOfSale);
      } else {
        showMessage("Failed", response?.message || "Failed to send product", "error");
      }
    } catch (error) {
      console.error("Errorsending product", error);
      showMessage("Error", "An unexpected error occurred", "error");
    } finally {
      setLoading(false); // Always stop loading
    }
  };

  const handleInwardSendRequest = async (request: any) => {
    try {
      setLoading(true);
      const sendData = {
        requestId: request.id,
        productId: request.productId,
        posId: request.requestToPOS,
        toPosId: request.requestFromPOS,
        quantity: request.quantity,
        note: request.note || "",
        documentDetails: request.documentDetails || ""
      };

      const response = await posService.UpdatePosinwardAccept(sendData);
      if (response?.isSuccess) {
        showMessage("Success", response.message, "success");
        // setShowRequestsDialog(false);
        await refreshRequests(pointOfSale);
        onAddSuccess(pointOfSale);
      } else {
        showMessage("Failed", response?.message || "Failed to accept request", "error");
      }
    } catch (error) {
      console.error("Error accepting inward request", error);
      showMessage("Error", "An unexpected error occurred", "error");
    } finally {
      setLoading(false); // Always stop loading
    }
  };

  const handleInwardRejectRequest = async (request: any) => {
    try {
      setLoading(true);
      const rejectData = {
        requestId: request.id,
        productId: request.productId,
        posId: request.requestToPOS,
        toPosId: request.requestFromPOS,
        quantity: request.quantity,
        note: request.note || ""
      };

      const response = await posService.UpdatePosinwardReject(rejectData);
      if (response?.isSuccess) {
        showMessage("Success", response.message, "success");
        // setShowRequestsDialog(false);
        await refreshRequests(pointOfSale);
      } else {
        showMessage("Failed", response?.message || "Failed to reject request", "error");
      }
    } catch (error) {
      console.error("Error rejecting inward request", error);
      showMessage("Error", "An unexpected error occurred", "error");
    } finally {
      setLoading(false); // Always stop loading
    }
  };

  const refreshRequests = async (selectedPosId: any) => {
    try {
      const response = await posService.Getposrequest(selectedPosId);
      if (response) {
        setRequestCount(response.length);
      } else {
        setRequestCount(0);
      }
      const response1 = await posService.GetposInwardrequest(selectedPosId);
      setinwardRequestCount(response1?.length || 0);
    } catch (error) {
      console.error("Failed to fetch requests count", error);
      setRequestCount(0);
      setinwardRequestCount(0);
    }
  };

  useEffect(() => {
    if (typeof onStatusFilterChange === "function") {
      onStatusFilterChange(selectedStatuses);
    }
  }, [JSON.stringify(selectedStatuses)]);

  return (
    <div className="col-12" style={{ width: "100%", padding: "0px", marginBottom: "0.5rem" }}>
      <Toast ref={toast} />
      <div className="card border-noround p-2" style={{ minHeight: "3.5rem" }}>
        <div className="grid align-items-center">

          {/* POS Dropdown */}
          <div className="col-12 md:col-6">
            <div className="flex flex-wrap gap-3">
              {(posData || []).map((pos: any) => (
                <div key={pos.posID} className="flex align-items-center gap-2">
                  <Checkbox
                    inputId={`pos-${pos.posID}`}
                    value={pos.posID}
                    disabled={posData.length === 1 && true}
                    onChange={(e) => {
                      // handlePOSChange(e);
                      const val = e.value;
                      setIsPOSSelected((prev: any) =>
                        prev.includes(val)
                          ? prev.filter((p: any) => p !== val)
                          : [...prev, val]
                      );
                    }}
                    checked={isPOSSelected.includes(pos.posID)}
                  />
                  <label htmlFor={`pos-${pos.posID}`}>{pos.posCode}</label>
                </div>
              ))}
            </div>

            {!isPOSSelected && pointOfSale.length === 0 && (
              <small className="p-error">POS is required</small>
            )}
          </div>

          {/* Search Input */}
          <div className="col-12 md:col-2">
            <div className="p-input-icon-left w-full">
              <i className="pi pi-search" />
              <InputText
                type="search"
                value={keywordsearch}
                onChange={(e) => {
                  const value = e.target.value;
                  setkeywordsearch(value);
                  onSearch(value);
                }}
                placeholder={t("Product search")}
                className="w-full"
                style={{
    border: "1px solid #75bb8aff",
    boxShadow: "0 0 0 1px #75bb8aff",
  }}
              />
            </div>
          </div>

      
          {/* Reset Button */}
          <div className="col-6 md:col-1">
            <Button
              label="Reset"
              onClick={resetSearchFields}
              severity="secondary"
              className="w-full"
              text
              raised
            />
          </div>

          {/* Add Product + Bell Button */}
          <div className="col-12 md:col-2 ml-auto flex justify-content-end gap-2 mt-2 md:mt-0">


            <Button
  label="Add Product"
  className="bg-blue-700 text-white border-round"
  onClick={() => setShowAddProductModal(true)}
  text
  raised
  disabled={isPOSSelected.length === 0} // ✅ Disable when no POS selected
  tooltip={
    isPOSSelected.length === 0
      ? "Please select at least one POS to add products."
      : "Add new product to the selected POS."
  }
  tooltipOptions={{ position: "left" }}
/>


            <div style={{ position: "relative", display: "inline-block" }}>
              <Button
                type="button"
                icon="pi pi-bell"
                severity="secondary"
                disabled={!pointOfSale}
                onClick={() => totalRequestCount > 0 && setShowRequestsDialog(true)}
              />
              {totalRequestCount > 0 && (
                <Badge
                  value={totalRequestCount}
                  severity="danger"
                  style={{
                    position: "absolute",
                    top: "-8px",
                    right: "-8px"
                  }}
                />
              )}
            </div>
          </div>
          <div className="col-12 mt-2">
            <div className="flex flex-wrap gap-3 mt-2">
              {statusOptions.map((status) => (
                <div key={status} className="flex align-items-center gap-2">
                  <Checkbox
                    inputId={`status-${status}`}
                    checked={selectedStatuses.includes(status)}
                    onChange={() =>
                      setSelectedStatuses((prev) =>
                        prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
                      )
                    }
                  />
                  <label htmlFor={`status-${status}`} className="m-0" style={{ marginLeft: 6 }}>
                    {status}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dialogs */}
        <Dialog
          visible={showAddProductModal}
          className="quickview w-30"
          breakpoints={{ "960px": "75vw", "641px": "90vw" }}
          modal
          onHide={() => setShowAddProductModal(false)}
        >
          <AddProducttopos posId={pointOfSale} onSuccess={handleOnSuccess} />
        </Dialog>

        <IncomingRequestsDialog
          visible={showRequestsDialog}
          onHide={() => setShowRequestsDialog(false)}
          posId={pointOfSale}
          onSendRequest={handleSendRequest}
          onRejectRequest={handleRejectRequest}
          onSendinwardRequest={handleInwardSendRequest}
          onRejectinwardRequest={handleInwardRejectRequest}
          setLoading={setLoading}
        />
      </div>
    </div>
  );

};
export default PosSearch;
